import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, TrendingDown, Minus, Calendar, BarChart3, Activity } from 'lucide-react';
import { HistoricalGranularity, getHistoricalData } from '../data/historicalData';
export function HistoricalPerformanceTab() {
  const [granularity, setGranularity] = useState<HistoricalGranularity>('Day');
  const {
    dataPoints,
    summaries,
    benchmarks
  } = getHistoricalData(granularity);
  const [activeMetric, setActiveMetric] = useState<'dials' | 'conversations' | 'creditPulls' | 'applications' | 'submissions' | 'locks'>('locks');
  const metricLabels = {
    dials: 'Dials',
    conversations: 'Conversations',
    creditPulls: 'Credit Pulls',
    applications: 'Applications',
    submissions: 'Submissions',
    locks: 'Locks'
  };
  const metricColors = {
    dials: '#0ea5e9',
    conversations: '#8b5cf6',
    creditPulls: '#f59e0b',
    applications: '#10b981',
    submissions: '#3b82f6',
    locks: '#ec4899' // pink-500
  };
  return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-65">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50" data-id="element-66">
        <div className="flex items-center gap-2" data-id="element-67">
          <Calendar className="w-5 h-5 text-slate-400" data-id="element-68" />
          <h2 className="text-lg font-semibold text-white" data-id="element-69">
            Historical Trends
          </h2>
        </div>
        <div className="flex bg-slate-900 rounded-lg p-1 border border-slate-700" data-id="element-70">
          {(['Day', 'Week', 'Month'] as HistoricalGranularity[]).map(g => <button key={g} onClick={() => setGranularity(g)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${granularity === g ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`} data-id="element-71">
              {g}
            </button>)}
        </div>
      </div>

      {/* Main Chart Section */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-72">
        {/* Metric Selector Sidebar */}
        <div className="lg:col-span-1 space-y-3" data-id="element-73">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-2" data-id="element-74">
            Select Metric
          </h3>
          {(Object.keys(metricLabels) as Array<keyof typeof metricLabels>).map(key => {
          const summary = summaries.find(s => s.metric === metricLabels[key]);
          const isActive = activeMetric === key;
          return <button key={key} onClick={() => setActiveMetric(key)} className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${isActive ? 'bg-slate-800 border-slate-600 shadow-md' : 'bg-slate-900/50 border-slate-800 hover:bg-slate-800/80 hover:border-slate-700'}`} data-id="element-75">
                  <div className="flex justify-between items-center mb-2" data-id="element-76">
                    <span className={`font-semibold ${isActive ? 'text-white' : 'text-slate-300'}`} data-id="element-77">
                      {metricLabels[key]}
                    </span>
                    {summary?.trend === 'up' && <TrendingUp className="w-4 h-4 text-emerald-400" data-id="element-78" />}
                    {summary?.trend === 'down' && <TrendingDown className="w-4 h-4 text-red-400" data-id="element-79" />}
                    {summary?.trend === 'neutral' && <Minus className="w-4 h-4 text-slate-400" data-id="element-80" />}
                  </div>
                  <div className="flex justify-between items-end" data-id="element-81">
                    <div data-id="element-82">
                      <div className="text-xs text-slate-500" data-id="element-83">
                        Avg / {granularity}
                      </div>
                      <div className="text-xl font-bold text-slate-200" data-id="element-84">
                        {summary?.average}
                      </div>
                    </div>
                    <div className="text-right" data-id="element-85">
                      <div className="text-xs text-slate-500" data-id="element-86">Best</div>
                      <div className="text-sm font-medium text-emerald-400" data-id="element-87">
                        {summary?.best}
                      </div>
                    </div>
                  </div>
                </button>;
        })}
        </div>

        {/* Chart Area */}
        <div className="lg:col-span-3 bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 flex flex-col" data-id="element-88">
          <div className="flex justify-between items-center mb-6" data-id="element-89">
            <div data-id="element-90">
              <h3 className="text-xl font-bold text-white flex items-center gap-2" data-id="element-91">
                <Activity className="w-5 h-5 text-cyan-400" data-id="element-92" />
                {metricLabels[activeMetric]} Trend
              </h3>
              <p className="text-sm text-slate-400 mt-1" data-id="element-93">
                Comparing actual performance against {granularity.toLowerCase()}
                ly benchmark
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-94">
              <div className="flex items-center gap-2" data-id="element-95">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: metricColors[activeMetric]
              }} data-id="element-96"></div>
                <span className="text-slate-300" data-id="element-97">Actual</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-98">
                <div className="w-4 h-0.5 bg-amber-400/80 border-t border-dashed border-amber-400" data-id="element-99"></div>
                <span className="text-slate-300" data-id="element-100">
                  Benchmark ({benchmarks[activeMetric]})
                </span>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-[400px]" data-id="element-101">
            <ResponsiveContainer width="100%" height="100%" data-id="element-102">
              <LineChart data={dataPoints} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-103">
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} data-id="element-104" />
                <XAxis dataKey="period" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} dy={10} data-id="element-105" />
                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} dx={-10} data-id="element-106" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f172a',
                borderColor: '#334155',
                borderRadius: '0.5rem',
                color: '#f8fafc'
              }} itemStyle={{
                color: metricColors[activeMetric],
                fontWeight: 'bold'
              }} data-id="element-107" />
                <ReferenceLine y={benchmarks[activeMetric]} stroke="#fbbf24" strokeDasharray="5 5" opacity={0.8} data-id="element-108" />
                <Line type="monotone" dataKey={activeMetric} name={metricLabels[activeMetric]} stroke={metricColors[activeMetric]} strokeWidth={3} dot={{
                r: 4,
                fill: '#0f172a',
                strokeWidth: 2
              }} activeDot={{
                r: 6,
                strokeWidth: 0
              }} animationDuration={1000} data-id="element-109" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Conversion Rates Over Time */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6" data-id="element-110">
        <div className="flex items-center gap-2 mb-6" data-id="element-111">
          <BarChart3 className="w-5 h-5 text-purple-400" data-id="element-112" />
          <h3 className="text-lg font-bold text-white" data-id="element-113">
            Conversion Rates Over Time
          </h3>
        </div>
        <div className="h-[300px]" data-id="element-114">
          <ResponsiveContainer width="100%" height="100%" data-id="element-115">
            <BarChart data={dataPoints} margin={{
            top: 20,
            right: 30,
            left: 0,
            bottom: 0
          }} data-id="element-116">
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} data-id="element-117" />
              <XAxis dataKey="period" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} dy={10} data-id="element-118" />
              <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} dx={-10} tickFormatter={val => `${val}%`} data-id="element-119" />
              <Tooltip contentStyle={{
              backgroundColor: '#0f172a',
              borderColor: '#334155',
              borderRadius: '0.5rem',
              color: '#f8fafc'
            }} formatter={(value: number) => [`${value}%`, undefined]} data-id="element-120" />
              <Legend wrapperStyle={{
              paddingTop: '20px'
            }} data-id="element-121" />
              <Bar dataKey="pullToApp" name="Pull → App" fill="#f59e0b" radius={[4, 4, 0, 0]} stackId="a" data-id="element-122" />
              <Bar dataKey="appToSub" name="App → Sub" fill="#10b981" radius={[4, 4, 0, 0]} stackId="b" data-id="element-123" />
              <Bar dataKey="subToSign" name="Sub → Sign" fill="#3b82f6" radius={[4, 4, 0, 0]} stackId="c" data-id="element-124" />
              <Bar dataKey="signToLock" name="Sign → Lock" fill="#8b5cf6" radius={[4, 4, 0, 0]} stackId="d" data-id="element-125" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>;
}